import React, { useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import useIsomorphicStyles from 'isomorphic-style-loader/useStyles';
import { AccordionSummary, AccordionDetails, TextField } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import SpinnerButton from 'components/SpinnerButton';
import ProgressWithPercentage from 'components/ProgressWithPercentage';
import Button from '@material-ui/core/Button';
import PulseLoader from 'react-spinners/PulseLoader';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import RecipeConversionDelete from 'components/RecipeConversionDelete';
import { useDispatch, useSelector } from 'react-redux';
import {
  setConvertedIngredients,
  deleteConversion,
  toggleConversionEditOpen,
} from 'actions/conversion';

import RecipeConversionStyles from './RecipeConversion.scss';

const CONVERT_TIMEOUT = 30;

const CONVERT_RECIPE_USING_QUEUE = gql`
  mutation ConvertRecipeUsingQueue(
    $id: String!
    $prompt: String
    $model: String
    $conversionType: String
  ) {
    ConvertRecipeUsingQueue(
      id: $id
      prompt: $prompt
      model: $model
      conversionType: $conversionType
    )
  }
`;

const SAVE_CONVERTED_RECIPE = gql`
  mutation SaveConvertedRecipe($id: String!, $ingredients: String) {
    SaveConvertedRecipe(id: $id, ingredients: $ingredients)
  }
`;

const DELETE_CONVERTED_RECIPE = gql`
  mutation DeleteConvertedRecipe($id: String!) {
    DeleteConvertedRecipe(id: $id)
  }
`;

const GET_CONVERTED_RECIPE = gql`
  query GetConvertedRecipe($id: String!) {
    GetConvertedRecipe(id: $id) {
      id
      ingredients_raw
    }
  }
`;

const RecipeConversion = () => {
  useIsomorphicStyles(RecipeConversionStyles);
  const dispatch = useDispatch();
  const _openAIPrompt = useSelector(state => state.conversion.openAIPrompt);
  const contentId = useSelector(state => state.conversion.id);
  const canConvertRecipe = useSelector(state => state.conversion.canConvertRecipe);
  const measurementSystem = useSelector(state => state.conversion.measurementSystem);
  const convertedIngredients = useSelector(state => state.conversion.convertedIngredients);
  const allowedToEditConversion =
    useSelector(state => state.conversion.allowedToEditConversion) || false;

  const [editableIngredients, setEditableIngredients] = useState(convertedIngredients?.join('\n'));
  const [openAiPrompt, setOpenAiPrompt] = useState(_openAIPrompt);
  const [conversionError, setConversionError] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [showConversionEditOptions, setShowConversionEditOptions] = useState(
    convertedIngredients !== null,
  );
  const [selectedModel, setSelectedModel] = useState('gpt-4o');

  const modelOptions = [
    { id: 'gpt-4o', title: 'gpt-4o' },
    { id: 'gpt-4-turbo-preview', title: 'gpt-4-turbo-preview' },
    { id: 'gpt-4', title: 'gpt-4' },
  ];
  const [saveConvertedRecipe, { loading: isSaving }] = useMutation(SAVE_CONVERTED_RECIPE);

  const [deleteConvertedRecipe, { loading: isDeleting }] = useMutation(DELETE_CONVERTED_RECIPE);

  const { startPolling, stopPolling } = useQuery(GET_CONVERTED_RECIPE, {
    variables: { id: contentId },
    skip: !contentId || !isConverting,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: data => {
      if (data?.GetConvertedRecipe?.ingredients_raw[0] !== null) {
        setEditableIngredients(data.GetConvertedRecipe.ingredients_raw.join('\n'));
        dispatch(setConvertedIngredients({ ingredients: data.GetConvertedRecipe.ingredients_raw }));
        setIsConverting(false);
        stopPolling();
        setShowConversionEditOptions(true);
      }
    },
    onError: error => {
      console.error('Error converting recipe:', error);
      setConversionError(true);
      setIsConverting(false);
      stopPolling();
    },
  });

  const [convertRecipe, { loading: isSubmitting }] = useMutation(CONVERT_RECIPE_USING_QUEUE, {
    onCompleted: () => {
      startPolling(1000);
      setIsConverting(true);
    },
    onError: error => {
      console.error('Error converting recipe:', error);
      setConversionError(true);
      setIsConverting(false);
    },
  });

  const handleOpenAiPromptChange = event => {
    setOpenAiPrompt(event.target.value);
  };

  // Callback when the input changes
  const handleIngredientsChange = event => {
    const newValue = event.target.value;
    setEditableIngredients(newValue);
    dispatch(setConvertedIngredients({ ingredients: newValue.split('\n') }));
  };

  const parseLines = value => (value ? value.replace(/(\\n)/g, '\n') : '');
  const initiateConversion = async () => {
    setConversionError(false);
    try {
      await deleteConvertedRecipe({ variables: { id: contentId } });
      await convertRecipe({
        variables: {
          id: contentId,
          prompt: openAiPrompt,
          model: selectedModel,
          conversionType: 'custom-prompt',
        },
      });
    } catch (initiateConversionError) {
      console.error('Error initiating conversion:', initiateConversionError);
      setConversionError(true);
    }
  };

  const handleSaveConversion = async () => {
    setConversionError(false);
    try {
      await saveConvertedRecipe({
        variables: {
          id: contentId,
          ingredients: editableIngredients,
        },
      });
    } catch (error) {
      setConversionError(true);
    }
  };

  const handleDeleteCompleted = () => {
    dispatch(deleteConversion());
  };

  const handleModelChange = event => {
    setSelectedModel(event.target.value);
  };

  if (!allowedToEditConversion || !measurementSystem || !_openAIPrompt) {
    return null;
  }

  return (
    <div className="recipe-conversion">
      <AccordionSummary
        expandIcon={<CloseIcon />}
        aria-controls="recipe-conversion-content"
        id="recipe-conversion-header"
        onClick={() => {
          dispatch(toggleConversionEditOpen());
        }}
      >
        <div className="heading">
          {canConvertRecipe
            ? 'RECIPE CONVERSION'
            : `This recipe cannot be converted. (Measurement Units: ${measurementSystem})`}
        </div>
      </AccordionSummary>
      <AccordionDetails>
        <div className="recipe-conversion__content">
          <>
            <div className="content_raw__openai">
              <div className="heading">Open AI</div>
              <div className="recipe-conversion__convert">
                {isConverting &&
                  !conversionError && (
                    <div className="recipe-conversion__progress">
                      <ProgressWithPercentage seconds={CONVERT_TIMEOUT} />
                    </div>
                  )}
                {conversionError && (
                  <div className="recipe-conversion__error">Please try later</div>
                )}
                <Select onChange={handleModelChange} value={selectedModel}>
                  {modelOptions.map(option => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.title}
                    </MenuItem>
                  ))}
                </Select>
                <SpinnerButton
                  size="medium"
                  text="Convert Recipe"
                  onClick={initiateConversion}
                  disabled={!canConvertRecipe || isSubmitting || isConverting || isDeleting}
                />
              </div>
            </div>
            <TextField
              className="margin-top-10"
              fullWidth
              multiline
              minRows={10}
              label="Prompt"
              variant="outlined"
              value={parseLines(openAiPrompt)}
              onChange={handleOpenAiPromptChange}
            />
          </>
          <div className="recipe-conversion__edit-options">
            {!showConversionEditOptions && (
              <Button
                className="recipe-conversion__activator"
                color="primary"
                size="large"
                variant="outlined"
                disabled={!convertedIngredients}
                onClick={() => setShowConversionEditOptions(true)}
                fullWidth
              >
                Edit Conversion
              </Button>
            )}
            {showConversionEditOptions && (
              <div>
                <TextField
                  fullWidth
                  multiline
                  variant="outlined"
                  value={editableIngredients}
                  onChange={handleIngredientsChange}
                  margin="normal"
                  maxRows={10}
                />
                <div className="recipe-conversion__delete">
                  <RecipeConversionDelete
                    id={contentId}
                    onDeleteCompleted={handleDeleteCompleted}
                  />
                </div>
                <div className="recipe-conversion__edit-buttons">
                  <Button
                    className="recipe-conversion__button"
                    onClick={() => setShowConversionEditOptions(false)}
                    size="large"
                    variant="contained"
                  >
                    Cancel
                  </Button>
                  <span>
                    <Button
                      className="recipe-conversion__button"
                      color="primary"
                      onClick={handleSaveConversion}
                      disabled={!editableIngredients || editableIngredients.trim().length === 0}
                      size="large"
                      type="submit"
                      variant="contained"
                    >
                      <PulseLoader loading={isSaving} color="#fff" sizeUnit="px" size={10} />
                      Save Conversion
                    </Button>
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </AccordionDetails>
    </div>
  );
};

const RecipeConversionWrapper = () => {
  const openAIPrompt = useSelector(state => state.conversion.openAIPrompt);
  const isConversionEditOpen = useSelector(state => state.conversion.isConversionEditOpen);
  return openAIPrompt?.length > 0 && isConversionEditOpen ? <RecipeConversion /> : null;
};

export default RecipeConversionWrapper;
